// src/components/map/MapSidebar.js
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './MapSidebar.css';
//import { PropertyTypeNumericToStringMap } from '../../utils/enums'; // Import the map
import { getFullImageUrl } from '../../utils/config'; // Assuming getFullImageUrl handles base path
import { useDevLogging } from '../../hooks/useDevLogging';
import defaultLogo from '../../assets/images/logo.png'; // Import the default logo

const MapSidebarComponent = ({
  isSearching,
  propertiesToDisplay,
  searchRadius,
  clickedNeighborhood,
  selectedLocationDetails,
  formatCurrencyForDisplay,
  selectedStateAbbr,
  title, // Added title prop for custom headings
  onPropertySelect,
}) => {
  useDevLogging('MapSidebar.js', {
    isSearching,
    propertiesToDisplay,
    searchRadius,
    clickedNeighborhood,
    selectedLocationDetails,
    formatCurrencyForDisplay,
    selectedStateAbbr,
    title,
    onPropertySelect,
  });
  const { t } = useTranslation();

  // Determine the heading for the results section
  let resultsHeading = t('mapSidebar.mapResults'); // Default heading
  if (title) {
    // If a specific title is provided (e.g., "My Saved Homes"), use it.
    resultsHeading = title;
  } else if (selectedStateAbbr) {
    resultsHeading = t('mapSidebar.propertiesInState', { state: selectedStateAbbr });
  } else if (searchRadius > 0 && selectedLocationDetails) {
    // Ensure selectedLocationDetails for radius context
    resultsHeading = t('mapSidebar.propertiesInRadius', { radius: searchRadius });
  } else if (clickedNeighborhood) {
    resultsHeading = t('mapSidebar.propertiesInNeighborhood', { neighborhood: clickedNeighborhood.name || t('mapSidebar.selectedNeighborhood') });
  } else if (!isSearching && propertiesToDisplay.length > 0) {
    // If no specific filter, but properties are shown (e.g., all properties initially or after clearing filters)
    resultsHeading = t('mapSidebar.displayingProperties', { count: propertiesToDisplay.length });
  }

  // Determine if a search context is active
  const isSearchActive = searchRadius > 0 || !!clickedNeighborhood || !!selectedStateAbbr;

  // Determine if the "No properties found" message should show
  // Show "No properties found" if a search is active (state, radius, neighborhood) and no properties match.
  const shouldShowNoResults = !isSearching && propertiesToDisplay.length === 0 && isSearchActive;

  return (
    <div className="map-sidebar-container">
      {/* Property Results Section */}
      {isSearching && (
        <div className="loading-indicator sidebar-section">
          <p>{t('mapSidebar.searching')}</p>
        </div>
      )}

      {!isSearching && propertiesToDisplay.length > 0 && (
        <div className="property-results sidebar-section">
          <h3>{resultsHeading}</h3>
          <div className="search-results-grid">
            {propertiesToDisplay.map((property) => {
              const addressParts = [property.address, property.city, property.state].filter(Boolean);
              const addressString = addressParts.length > 0 ? addressParts.join(', ') : t('mapSidebar.addressNotAvailable');

              const specs = [
                property.beds ? t('mapSidebar.beds', { count: property.beds }) : null,
                property.baths ? t('mapSidebar.baths', { count: property.baths }) : null,
                property.size ? t('mapSidebar.sqft', { size: property.size.toLocaleString() }) : null,
              ]
                .filter(Boolean)
                .join(` ${t('mapSidebar.separator')} `);

              return (
                <div
                  key={property.propertyID || property._id || Math.random()}
                  className="property-card"
                  onClick={() => onPropertySelect(property.propertyID)} // Use the callback
                >
                  <div className="property-card-image-container">
                    <img
                      src={property.mainImageURL ? getFullImageUrl(property.mainImageURL) : defaultLogo}
                      alt={t('mapSidebar.thumbnailAlt', { address: property.address || t('mapSidebar.property') })}
                      className="property-card-thumbnail"
                      onError={(e) => {
                        e.target.src = defaultLogo; // Fallback to default logo if image fails to load
                      }}
                    />
                    <div className="property-card-price">{property.price ? formatCurrencyForDisplay(property.price) : t('mapSidebar.priceNotAvailable')}</div>
                  </div>
                  <div className="property-card-info">
                    <div className="property-card-specs">{specs || t('mapSidebar.detailsNotAvailable')}</div>
                    <div className="property-card-address" style={{ direction: 'ltr' }}>
                      {addressString}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {shouldShowNoResults && (
        <div className="no-results-message sidebar-section">
          <p>{t('mapSidebar.noResults')}</p>
          {selectedStateAbbr && <p>{t('mapSidebar.noResultsState')}</p>}
          {!selectedStateAbbr && (searchRadius > 0 || clickedNeighborhood) && <p>{t('mapSidebar.noResultsRadius')}</p>}
          {!selectedStateAbbr && !searchRadius && !clickedNeighborhood && <p>{t('mapSidebar.noResultsExplore')}</p>}
        </div>
      )}
    </div>
  );
};

// Define propTypes for the base component
MapSidebarComponent.propTypes = {
  isSearching: PropTypes.bool,
  propertiesToDisplay: PropTypes.array,
  searchRadius: PropTypes.number,
  clickedNeighborhood: PropTypes.object,
  selectedLocationDetails: PropTypes.object,
  formatCurrencyForDisplay: PropTypes.func.isRequired,
  selectedStateAbbr: PropTypes.string,
  title: PropTypes.string,
  onPropertySelect: PropTypes.func.isRequired,
};

// Memoize to prevent unnecessary re-renders when Map parent re-renders
const MapSidebar = React.memo(MapSidebarComponent);

export default MapSidebar;
